import React, { useState } from 'react';
import { useGetSalesCountByOrderStatusQuery } from "Services/Admin/DashboardApiSlice";

export const OrderStatusBars = ({ mockMultiplier = 1 }: { mockMultiplier?: number }) => {
  const plant = process.env.REACT_APP_PLANT;
  const { data: statusData, isLoading } = useGetSalesCountByOrderStatusQuery({ plant });

  const [statusFilter, setStatusFilter] = useState("All");
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  const getCount = (label: string) => {
    if (!statusData || isLoading) return 0;
    return statusData[label] || 0;
  };

  const applyMock = (val: number) => Math.max(1, Math.round(val * mockMultiplier));

  const rawStatuses = [
    { label: "Pending", count: applyMock(getCount("Pending") || 150) },
    { label: "Processing", count: applyMock(getCount("Processing") || 420) },
    { label: "Shipped", count: applyMock(getCount("Shipped") || 600) },
    { label: "Delivered", count: applyMock(getCount("Delivered") || 2450) },
    { label: "Cancelled", count: applyMock(getCount("Cancelled") || 45) },
    { label: "Returned", count: applyMock(getCount("Returned") || 85) },
  ];

  const filteredStatuses = rawStatuses.filter(s => statusFilter === "All" || s.label === statusFilter);
  const totalOrders = filteredStatuses.reduce((acc, curr) => acc + curr.count, 0);

  const displayStatuses = filteredStatuses.map(status => {
    const percentage = totalOrders > 0 ? Math.round((status.count / totalOrders) * 100) : 0;
    return { ...status, percentage, width: `${percentage}%` };
  });

  const handleRowClick = (statusLabel: string) => {
    const targetId = `status-${statusLabel.toLowerCase()}`;
    const element = document.getElementById(targetId);
    
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedRow(targetId);
      setTimeout(() => setHighlightedRow(null), 1500);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        
        {/* Responsive Header: REMOVED DUPLICATE DATE FILTERS */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-3 border-b border-gray-100 mb-5 gap-4">
          <h3 className="text-[15px] font-bold text-[#1B2559] flex items-center gap-2">
            Order Status
          </h3>
          
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-[#1B2559] text-[11px] font-bold rounded-md px-3 py-1.5 outline-none cursor-pointer hover:bg-gray-100"
          >
            <option value="All">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Returned">Returned</option>
          </select>
        </div>
        
        <div className="space-y-3">
          {displayStatuses.map((status, idx) => {
            const rowId = `status-${status.label.toLowerCase()}`;
            const isHighlighted = highlightedRow === rowId;

            return (
              <div 
                key={idx} 
                id={rowId}
                onClick={() => handleRowClick(status.label)}
                title={`Focus on ${status.label}`}
                className={`flex items-center justify-between group cursor-pointer p-1.5 -mx-1.5 rounded transition-all duration-300 ${
                  isHighlighted ? 'bg-blue-50 ring-1 ring-blue-100' : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-[13px] font-bold text-[#1B2559] w-24 group-hover:text-[#1E5EFF] transition-colors">
                  {status.label}
                </span>
                
                <div className="flex-1 mx-4 h-3.5 bg-[#DCE6FA] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#1E5EFF] rounded-full transition-all duration-700 ease-out" 
                    style={{ width: status.width }}
                  ></div>
                </div>
                
                <span className="text-[13px] font-bold text-[#1B2559] w-24 text-right">
                  {status.count.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
        <div className="text-[13px] font-semibold text-[#1B2559] text-center">
          Avg Fulfillment Time <span className="font-extrabold ml-1">1.8 Days</span>
        </div>
        <div className="hidden sm:block w-px h-5 bg-gray-200"></div> 
        <div className="text-[13px] font-semibold text-[#1B2559] text-center">
          Avg Delivery Time <span className="font-extrabold ml-1">3.4 Days</span>
        </div>
      </div>
    </div>
  );
};